import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { firebaseReady } from '../lib/firebase';
import Reveal from '../components/Reveal';

const friendlyError = (code: string) =>
  code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')
    ? 'Wrong email or password.'
    : code.includes('email-already-in-use')
      ? 'That email already has an account — sign in instead.'
      : code.includes('weak-password')
        ? 'Password must be at least 6 characters.'
        : code.includes('invalid-email')
          ? "That email address doesn't look right."
          : code.includes('popup-closed')
            ? 'Google sign-in was closed before finishing.'
            : code.includes('operation-not-allowed')
              ? 'Google sign-in is not enabled yet — use email & password.'
              : 'Something went wrong — please try again.';

export default function Login() {
  const { user, loading, signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get('redirect') ?? '/account';

  const [mode, setMode] = useState<'signin' | 'signup'>(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  if (!firebaseReady) return <Navigate to="/" replace />;
  if (!loading && user) return <Navigate to={redirect} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setBusy(true);
    try {
      if (mode === 'signin') await signIn(form.email, form.password);
      else await signUp(form.name, form.email, form.phone, form.password);
      navigate(redirect, { replace: true });
    } catch (err: any) {
      setError(friendlyError(err?.code ?? ''));
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError('');
    setBusy(true);
    try {
      await signInWithGoogle();
      navigate(redirect, { replace: true });
    } catch (err: any) {
      setError(friendlyError(err?.code ?? ''));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!form.email) {
      setError('Enter your email above first, then click "Forgot password" again.');
      return;
    }
    setError('');
    try {
      await resetPassword(form.email);
      setInfo(`Password reset email sent to ${form.email} — check your inbox.`);
    } catch (err: any) {
      setError(friendlyError(err?.code ?? ''));
    }
  };

  return (
    <div className="container-x flex min-h-[70vh] items-center justify-center py-14">
      <Reveal className="w-full max-w-md">
        <div className="card p-7 sm:p-8">
          <h1 className="font-display text-2xl font-bold text-white">
            {mode === 'signin' ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="mt-1.5 text-sm text-zinc-400">
            {mode === 'signin'
              ? 'Sign in to see your bookings and saved vehicles.'
              : 'Track bookings, save your vehicles, and book faster next time.'}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="label">Your name</label>
                <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Jane Driver" autoComplete="name" />
              </div>
            )}
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
            </div>
            {mode === 'signup' && (
              <div>
                <label className="label">Phone (for booking confirmations)</label>
                <input className="input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(613) 555-0123" autoComplete="tel" />
              </div>
            )}
            <div>
              <label className="label">Password</label>
              <input className="input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} />
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}
            {info && <p className="text-sm text-emerald-400">{info}</p>}

            <button className="btn-primary w-full" disabled={busy}>
              {busy ? 'Working…' : mode === 'signin' ? <><KeyRound size={16} /> Sign In</> : <><UserPlus size={16} /> Create Account</>}
            </button>
          </form>

          <button onClick={google} disabled={busy} className="btn-ghost mt-3 w-full text-sm">
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.4-2.2 3.7-5.1 3.7-8.6z" />
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.1 0-5.8-2.1-6.7-5l-3.9 3C3.4 21.4 7.4 24 12 24z" />
              <path fill="#FBBC05" d="M5.3 14.4c-.3-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.9-3z" />
              <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.6l3.9 3c.9-2.9 3.6-4.9 6.7-4.9z" />
            </svg>
            Continue with Google
          </button>

          <div className="mt-5 flex items-center justify-between text-sm">
            <button
              className="text-zinc-500 hover:text-zinc-300"
              onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); setInfo(''); }}
            >
              {mode === 'signin' ? 'New here? Create an account' : 'Have an account? Sign in'}
            </button>
            {mode === 'signin' && (
              <button className="text-zinc-500 hover:text-zinc-300" onClick={forgot}>
                Forgot password?
              </button>
            )}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
